import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  isGcalConfigured,
  getCalendarAccessToken,
  listTodaysEvents,
  buildContextFromEvent,
  type CalendarEvent,
} from "@/lib/gcal";
import { getDateLocale } from "@/lib/dateFormat";

interface Props {
  onPick: (data: { title: string; context: string }) => void;
}

export function CalendarPicker({ onPick }: Props) {
  const { t } = useTranslation();
  const [events, setEvents] = useState<CalendarEvent[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isGcalConfigured()) return null;

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getCalendarAccessToken();
      const items = await listTodaysEvents(token);
      setEvents(items);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const pick = (event: CalendarEvent) => {
    onPick({
      title: event.summary || t("calendar.untitledEvent"),
      context: buildContextFromEvent(event),
    });
    setEvents(null);
  };

  return (
    <div className="bg-redi-surface/50 rounded-lg p-3 mb-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-redi-muted text-xs">
          {t("calendar.connectHint")}
        </span>
        <button
          onClick={load}
          disabled={loading}
          className="bg-redi-surface text-redi-text text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-redi-surface/80 disabled:opacity-50"
        >
          {loading ? "…" : t("calendar.import")}
        </button>
      </div>
      {error && <p className="text-redi-error text-xs mt-2">{error}</p>}
      {events != null && (
        <div className="mt-3">
          {events.length === 0 ? (
            <p className="text-redi-muted text-xs">{t("calendar.empty")}</p>
          ) : (
            <ul className="space-y-1">
              {events.map((e) => {
                const start = e.start
                  ? new Date(e.start).toLocaleTimeString(getDateLocale(), {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "";
                return (
                  <li key={e.id}>
                    <button
                      onClick={() => pick(e)}
                      className="w-full text-left bg-redi-dark hover:bg-redi-dark/80 rounded-lg px-3 py-2"
                    >
                      <span className="text-redi-text text-sm font-medium">
                        {e.summary || t("calendar.untitledEvent")}
                      </span>
                      <span className="block text-redi-muted text-xs">
                        {start}
                        {e.attendees.length > 0
                          ? ` · ${e.attendees.length} ${t("calendar.attendees")}`
                          : ""}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
